import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import Feather from "@expo/vector-icons/Feather";
import Head from "expo-router/head";
import React from "react";
import { useTranslation } from "react-i18next";
import { Linking, StyleSheet, TouchableOpacity, View } from "react-native";

type IconName = React.ComponentProps<typeof Feather>["name"];

type ContactRow = {
  key: string;
  icon: IconName;
  labelKey: string;
  value: string;
  url: string;
};

const SUPPORT_EMAIL = "soporte@vehilink.app";
const WHATSAPP_NUMBER = "595976790424";
const WHATSAPP_DISPLAY = "+595 976 790424";
const INSTAGRAM_HANDLE = "@vehilink.app";
const X_HANDLE = "@VehiLink";
const FACEBOOK_HANDLE = "facebook.com/vehilink.app";

const CONTACT_ROWS: ContactRow[] = [
  {
    key: "whatsapp",
    icon: "message-circle",
    labelKey: "HELP_WHATSAPP_LABEL",
    value: WHATSAPP_DISPLAY,
    url: `https://wa.me/${WHATSAPP_NUMBER}`,
  },
  {
    key: "email",
    icon: "mail",
    labelKey: "HELP_EMAIL_LABEL",
    value: SUPPORT_EMAIL,
    url: `mailto:${SUPPORT_EMAIL}`,
  },
];

const SOCIAL_ROWS: ContactRow[] = [
  {
    key: "instagram",
    icon: "instagram",
    labelKey: "HELP_INSTAGRAM_LABEL",
    value: INSTAGRAM_HANDLE,
    url: "https://instagram.com/vehilink.app",
  },
  {
    key: "x",
    icon: "twitter",
    labelKey: "HELP_X_LABEL",
    value: X_HANDLE,
    url: "https://x.com/VehiLink",
  },
  {
    key: "facebook",
    icon: "facebook",
    labelKey: "HELP_FACEBOOK_LABEL",
    value: FACEBOOK_HANDLE,
    url: "https://facebook.com/vehilink.app",
  },
];

const HelpScreen = () => {
  const { t } = useTranslation();

  const renderRow = (row: ContactRow) => (
    <TouchableOpacity
      key={row.key}
      style={styles.row}
      onPress={() => Linking.openURL(row.url)}
      accessibilityRole="link"
      accessibilityLabel={`${t(row.labelKey)}: ${row.value}`}
    >
      <Feather
        name={row.icon}
        size={20}
        color="#1e3a8a"
        style={styles.rowIcon}
      />
      <View style={styles.rowText}>
        <ThemedText style={styles.rowLabel}>{t(row.labelKey)}</ThemedText>
        <ThemedText style={styles.rowValue}>{row.value}</ThemedText>
      </View>
    </TouchableOpacity>
  );

  return (
    <>
      <Head>
        <title>{t("PAGE_TITLE_HELP")}</title>
      </Head>
      <CustomHeader
        title={t("HELP")}
        type="subtitle"
        variant="tab"
        showBackButton
      />
      <ThemedView style={styles.container}>
        <ThemedText style={styles.description}>
          {t("HELP_DESCRIPTION")}
        </ThemedText>

        <ThemedText style={styles.sectionTitle}>
          {t("HELP_CONTACT_TITLE")}
        </ThemedText>
        {CONTACT_ROWS.map(renderRow)}

        <ThemedText style={[styles.sectionTitle, styles.sectionTitleSpacing]}>
          {t("HELP_FOLLOW_TITLE")}
        </ThemedText>
        {SOCIAL_ROWS.map(renderRow)}
      </ThemedView>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#4B5563",
    marginBottom: 8,
  },
  sectionTitleSpacing: {
    marginTop: 24,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
  },
  rowIcon: {
    marginRight: 16,
    width: 24,
    textAlign: "center",
  },
  rowText: {
    flex: 1,
  },
  rowLabel: {
    fontSize: 14,
    color: "#374151",
  },
  rowValue: {
    fontSize: 15,
    fontWeight: "500",
    marginTop: 2,
  },
});

export default HelpScreen;
